import { useEffect, useMemo, useState } from 'react';
import { Braces, ChevronRight, Code2, ListTree, UnfoldVertical } from 'lucide-react';
import type { JsonValue } from '@maland/contracts';
import { prettyJson } from '../lib/format';

type JsonPath = readonly (string | number)[];
type ViewMode = 'tree' | 'source';

function isContainer(value: JsonValue): value is readonly JsonValue[] | { readonly [key: string]: JsonValue } {
  return value !== null && typeof value === 'object';
}

function pathKey(path: JsonPath): string {
  return JSON.stringify(path);
}

function collectContainerPaths(value: JsonValue, path: JsonPath, output: Set<string>, maxDepth?: number): void {
  if (!isContainer(value) || (maxDepth !== undefined && path.length > maxDepth)) return;
  output.add(pathKey(path));
  if (Array.isArray(value)) {
    value.forEach((child, index) => collectContainerPaths(child, [...path, index], output, maxDepth));
  } else {
    Object.entries(value).forEach(([key, child]) => collectContainerPaths(child, [...path, key], output, maxDepth));
  }
}

function containerLabel(value: readonly JsonValue[] | { readonly [key: string]: JsonValue }): string {
  const count = Array.isArray(value) ? value.length : Object.keys(value).length;
  return Array.isArray(value) ? `Array(${count})` : `Object(${count})`;
}

function leafClass(value: JsonValue): string {
  if (value === null) return 'null';
  return typeof value;
}

function leafLabel(value: JsonValue): string {
  if (typeof value === 'string') return JSON.stringify(value);
  if (value === null) return 'null';
  return String(value);
}

function JsonTreeNode({
  value,
  nodeKey,
  path,
  depth,
  expanded,
  onToggle,
}: {
  value: JsonValue;
  nodeKey?: string | number;
  path: JsonPath;
  depth: number;
  expanded: ReadonlySet<string>;
  onToggle: (path: JsonPath) => void;
}) {
  const keyLabel = nodeKey === undefined ? undefined : typeof nodeKey === 'number' ? nodeKey : JSON.stringify(nodeKey);
  if (!isContainer(value)) {
    return <div className="json-tree__row json-tree__row--leaf" style={{ '--json-depth': depth } as React.CSSProperties}>
      <span className="json-tree__spacer" />
      {keyLabel !== undefined && <><span className="json-tree__key">{keyLabel}</span><span className="json-tree__colon">:</span></>}
      <span className={`json-tree__value json-tree__value--${leafClass(value)}`}>{leafLabel(value)}</span>
    </div>;
  }

  const open = expanded.has(pathKey(path));
  const entries: readonly [string | number, JsonValue][] = Array.isArray(value)
    ? value.map((child, index) => [index, child] as const)
    : Object.entries(value);
  return <div className="json-tree__node">
    <button
      className="json-tree__row json-tree__toggle"
      style={{ '--json-depth': depth } as React.CSSProperties}
      aria-expanded={open}
      onClick={() => onToggle(path)}
    >
      <ChevronRight className={open ? 'json-tree__chevron--open' : ''} size={13} />
      {keyLabel !== undefined && <><span className="json-tree__key">{keyLabel}</span><span className="json-tree__colon">:</span></>}
      <span className="json-tree__container-label">{containerLabel(value)}</span>
    </button>
    {open && <div>{entries.map(([key, child]) => <JsonTreeNode value={child} nodeKey={key} path={[...path, key]} depth={depth + 1} expanded={expanded} onToggle={onToggle} key={String(key)} />)}</div>}
  </div>;
}

export function JsonInspector({ value, defaultExpandedDepth = 1 }: { value: JsonValue; defaultExpandedDepth?: number }) {
  const [mode, setMode] = useState<ViewMode>('tree');
  const defaultExpanded = useMemo(() => {
    const paths = new Set<string>();
    collectContainerPaths(value, [], paths, defaultExpandedDepth);
    return paths;
  }, [defaultExpandedDepth, value]);
  const allExpanded = useMemo(() => {
    const paths = new Set<string>();
    collectContainerPaths(value, [], paths);
    return paths;
  }, [value]);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(defaultExpanded);

  useEffect(() => {
    setMode('tree');
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const toggle = (path: JsonPath) => {
    const key = pathKey(path);
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return <div className="json-inspector">
    <div className="json-inspector__toolbar">
      <div className="json-view-toggle" role="tablist" aria-label="JSON 展示模式">
        <button role="tab" aria-selected={mode === 'tree'} onClick={() => setMode('tree')}><ListTree size={13} />可视化</button>
        <button role="tab" aria-selected={mode === 'source'} onClick={() => setMode('source')}><Code2 size={13} />源码</button>
      </div>
      {mode === 'tree' && <div className="json-expand-actions">
        <button onClick={() => setExpanded(allExpanded)}><UnfoldVertical size={13} />全部展开</button>
        <button onClick={() => setExpanded(new Set())}><Braces size={13} />全部收起</button>
      </div>}
    </div>
    {mode === 'tree'
      ? <div className="json-tree"><JsonTreeNode value={value} path={[]} depth={0} expanded={expanded} onToggle={toggle} /></div>
      : <pre className="json-source">{prettyJson(value)}</pre>}
  </div>;
}
